import * as Api from '@ViewModels';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import { useLocation } from 'react-router';
import { toast } from 'sonner';
import { v4 as uuidgen } from 'uuid';
import {
	ICampaignApprovalCommand,
	IContentCalendarSelectedSuggestion,
	ICreateCampaignRequest,
	ICreateSocialMediaPostRequest,
	IImpersonationContextComponentProps,
	ILocationState,
	IMultiCampaignSchedulerRequest,
	ImpersonationContextKey,
	ISequenceTransitionRequest,
	TemplateOrTemplateFilter,
} from '../../../models';
import { CampaignType } from '../../../models/AdminModels';
import { BlogPostRequest } from '../../../models/Blogs';
import {
	EDuplicateType,
	getDuplicateTemplateType,
	shouldShowDuplicateTemplateWarning,
	suppressDuplicateTemplateWarning,
} from '../../../models/Campaigns';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../models/hooks/appStateHooks';
import { usePersistChangeInBrowserStorage } from '../../../models/hooks/usePersistChangeInBrowserStorage';
import { ILocalNotification } from '../../../models/LocalNotifications';
import { Topics } from '../../../models/LocalNotificationTopics';
import { useEventLogging } from '../../../models/Logging';
import { getDisplayName } from '../../../models/NameUtils';
import { storagePrefixExcludedFromClear } from '../../../models/Storage';
import { getIndustryName } from '../../../models/utils';
import {
	getBlogPostAsync,
	getBlogPostTemplateAsync,
	invalidateAllContentCalendarSuggestions,
	invalidateBlogContentCalPendingApprovalQuery,
	invalidateEmailContentCalPendingApprovalQuery,
	invalidateSocialContentCalPendingApprovalQuery,
	useBlogContentCalApproveMutation,
	useBlogContentCalPendingApprovalQuery,
	useContentCalendarSuggestionFilterQueries,
	useContentCalendarSuggestionFilterQuery,
	useEmailContentCalApproveMutation,
	useEmailContentCalPendingApprovalQuery,
	useSocialContentCalApproveMutation,
	useSocialContentCalPendingApprovalQuery,
	useUserQuery,
} from '../../../queries';
import { CampaignsApprovalViewModel, IBlogTemplateCard, ITemplateCard } from '../../../viewmodels/AppViewModels';
import {
	ConfirmationDialog,
	DefaultConfirmationOptions,
	IConfirmationDialogOption,
} from '../../components/ConfirmationDialog';
import { CampaignCalendar, ICampaignCalendarComponent } from '../../components/campaigns/CampaignCalendar';
import {
	CampaignCalendarSuggestionsContext,
	useCreateCampaignCalendarSuggestionsContext,
} from '../../components/campaigns/CampaignCalendar/context';
import {
	EventDataType,
	ICampaignCalendarEventPlaceholder,
	ICampaignCalendarOptions,
} from '../../components/campaigns/CampaignCalendar/models';
import {
	DuplicateWarningDialog,
	IDuplicateWarningDialogProps,
} from '../../components/campaigns/DuplicateWarningDialog';
import { ISelectOption } from '../../components/DeprecatedSelect';
import { FabContext } from '../../components/FabContext';
import { MediaQueryObserver } from '../../components/helpers/mediaQuery';
import { LocalNotificationObserver } from '../../components/LocalNotificationObserver';
import { MultiContainerHeader } from '../../components/MultiContainerHeader';
import {
	MultipleSelect,
	MultipleSelectContent,
	MultipleSelectItem,
	MultipleSelectTrigger,
	MultipleSelectValue,
} from '../../components/MultipleSelect';
import { DefaultSelectBox, ISelectBoxOption } from '../../components/SelectBox';
import { ApproveAllIcon } from '../../components/svgs/icons/ApproveAllIcon';
import { CancelIconFill } from '../../components/svgs/icons/CancelIconFill';
import { WarningIcon } from '../../components/svgs/icons/WarningIcon';
import { Toggle } from '../../components/Toggle';
import { UserSelectModal } from '../../components/UserSelectModal';
import { AiButton } from '../../components-v2/AiButton';
import { Button } from '../../components-v2/Button';
import { useAppDrawerContext } from '../../components-v2/Drawer/AppDrawer';
import { Dropdown, DropdownContent, DropdownItem, DropdownSelectTrigger } from '../../components-v2/Dropdown';
import { THEME } from '../../components-v2/Theme/theme';
import { bs } from '../../styles/styles';
import { AiGeneratedContentSelectionModal } from './AiGeneratedContentSelectionModal';
import {
	ContentCalendarTemplateBrowser,
	IContentCalendarTemplateBrowserComponent,
} from './ContentCalendarTemplateBrowser';
import {
	CalendarAccountAgeOptions,
	CalendarType,
	getSuggestionCampaignType,
	isSuggestionInCampaignTypes,
} from './models';
import { ContentCalendarLegend, ContentCalendarTabs } from './presentation';
import { styleSheet } from './styles';

// Extended DropResult type to include additional data
interface ExtendedDropResult extends DropResult {
	source: DropResult['source'] & { data?: any };
}

interface Props extends IImpersonationContextComponentProps {
	calendarOptions?: ICampaignCalendarOptions;
	styles?: StyleDeclarationValue[];
	className?: string;
	hideTemplateBrowser?: boolean;
	droppableType?: string;
	onDragEnd?: (result: DropResult) => void;
	onOpenCampaignComposer?(template: Api.ITemplate, schedule?: Api.IScheduledSend, referrer?: string): boolean;
	onOpenMultiSuggestionsScheduler?(
		campaignType: CampaignType,
		suggestions: IContentCalendarSelectedSuggestion[],
		onFinish?: () => void,
		sequenceFlags?: { isSequenceStep?: boolean }
	): boolean;
	onOpenSequenceTransition?(transitionRequest: ISequenceTransitionRequest): boolean;
	onCampaignClicked?(e: React.MouseEvent<HTMLElement>, campaign: Api.CampaignViewModel): void;
	onBlogPostClicked?(e: React.MouseEvent<HTMLElement, MouseEvent>, blog: Api.IBlogReportView | IBlogTemplateCard): void;
	onCampaignGroupClicked?(e: React.MouseEvent<HTMLElement>, campaigns: Api.CampaignViewModel[]): void;
	/** @returns True if handled */
	onNotifyClientClicked?(campaign: EventDataType): void;
	onCampaignCalendarRef?(ref?: ICampaignCalendarComponent): void;
	onRenderHeaderAccessory?(): React.ReactNode;
	onSocialMediaPostClicked?(
		e: React.MouseEvent<HTMLElement, MouseEvent>,
		postReport: Api.SocialMediaPostReportViewModel
	): Promise<any>;
	onSuggestionClicked?(
		e: React.MouseEvent<HTMLElement, MouseEvent>,
		suggestion: Api.IContentCalendarSuggestion
	): Promise<any>;
	onSuggestedSocialMediaEventClicked?(
		e: React.MouseEvent<HTMLElement>,
		suggestion: Api.IContentCalendarSuggestion
	): Promise<any>;
	/** Hide the built-in user filter dropdown (used in admin mode) */
	hideUserFilter?: boolean;
	templateBrowserCampaignType: CampaignType;
	setTemplateBrowserCampaignType: (campaignType: CampaignType) => void;
	onSuggestionsToggleChanged?: (isOn: boolean) => void;
	onGeneratedContentToggleChanged?: (isOn: boolean) => void;
	onSelectedUserIdChanged?: (selectedUserId: string | null) => void;
}

export interface IContentCalendarComponent {
	loadAllCalendarData?(): Promise<any>;
	onDragEndInternal?(result: DropResult): Promise<void>;
}

const newAccountCutoffDays = 30;
const numMonthsInQuarterly = 3;
const droppableType = 'campaignTemplateCard';

// Filter options
enum UserFilterOptions {
	All = 'all',
	CurrentUser = 'currentuser',
	User = 'user',
}

enum StatusFilterOptions {
	All = 'all',
	Pending = 'pending',
	Scheduled = 'scheduled',
	Sent = 'sent',
	Cancelled = 'cancelled',
}

const _ContentCalendar = React.forwardRef<IContentCalendarComponent, Props>(function ContentCalendarBase(props, ref) {
	const userSession = useUserSession();

	const errorMessages = useErrorMessages();
	const location = useLocation<ICampaignApprovalCommand>();
	const { logApiError, logEvent } = useEventLogging('ContentCalendar');
	const fullscreenModal = useFullscreenModal();
	const [month, setMonth] = React.useState(moment().startOf('month').toDate());
	const [duplicateTemplateDialogProps, setDuplicateTemplateDialogProps] = React.useState<IDuplicateWarningDialogProps>({
		isOpen: false,
		onRequestClose: () => setDuplicateTemplateDialogProps(value => ({ ...value, isOpen: false })),
		title: 'Campaign Duplicate',
		type: CampaignType.Email,
		duplicateType: EDuplicateType.User,
		lastUsedDate: undefined,
	});
	const [calendarType, setCalendarType] = usePersistChangeInBrowserStorage<CalendarType>({
		storageType: 'localStorage',
		storageKey: `${storagePrefixExcludedFromClear}-content-calendar-calendar-type`,
		defaultIfEmpty: CalendarType.Quarterly,
		skipPersistFn: () => props.impersonationContext?.isValid,
	});
	const [isAiGeneratedContentSelectionModalOpen, setIsAiGeneratedContentSelectionModalOpen] = React.useState(false);
	const [selectedAccountAge, setSelectedAccountAge] = React.useState<
		ISelectOption<Api.ContentCalendarSuggestionAccountAge>
	>(() => {
		if (!props.impersonationContext) {
			const isNewAccount = moment(userSession.account.creationDate) > moment().subtract(newAccountCutoffDays, 'days');
			if (isNewAccount) {
				return CalendarAccountAgeOptions.find(
					option => option.dataContext === Api.ContentCalendarSuggestionAccountAge.New
				);
			}
		}
		return CalendarAccountAgeOptions[0];
	});

	const isImpersonating = props.impersonationContext?.account?.id;
	const isBlogsEnabled = isImpersonating
		? props.impersonationContext.account.features?.blogFeature?.enabled
		: userSession.account?.features?.blogFeature.enabled;

	const templateBrowserCampaignType = props.templateBrowserCampaignType;
	const setTemplateBrowserCampaignType = props.setTemplateBrowserCampaignType;

	const isSocialMediaEnabled = isImpersonating
		? props.impersonationContext.account.features?.socialMedia?.enabled
		: userSession.account?.features?.socialMedia?.enabled;

	const isComplianceEnabled = isImpersonating
		? props.impersonationContext.account.preferences?.complianceSettings?.enabled
		: userSession.account?.preferences?.complianceSettings?.enabled;

	const enabledTypes = React.useMemo<CampaignType[]>(() => {
		const types = [CampaignType.Email];
		if (isSocialMediaEnabled) {
			types.push(CampaignType.Social);
		}
		if (isBlogsEnabled) {
			types.push(CampaignType.Blog);
		}
		return types;
	}, [isBlogsEnabled, isSocialMediaEnabled]);
	const [campaignTypes, setCampaignTypes] = usePersistChangeInBrowserStorage<CampaignType[]>({
		storageKey: `${storagePrefixExcludedFromClear}-content-calendar-campaign-type`,
		storageType: 'localStorage',
		defaultIfEmpty: enabledTypes,
		skipPersistFn: () => props.impersonationContext?.isValid,
	});

	const [placeholders, setPlaceholders] = React.useState<ICampaignCalendarEventPlaceholder<ITemplateCard>[]>([]);
	const [showSuggestionsToggleValue, setShowSuggestionsToggleValue] = usePersistChangeInBrowserStorage<boolean>({
		storageType: 'localStorage',
		storageKey: `${storagePrefixExcludedFromClear}-content-calendar-suggestion-toggle`,
		skipPersistFn: () => props.impersonationContext?.isValid,
		defaultIfEmpty: false,
	});
	const [showSuggestionsAiGeneratedToggleValue, setShowSuggestionsAiGeneratedToggleValue] =
		usePersistChangeInBrowserStorage<boolean>({
			storageType: 'localStorage',
			storageKey: `${storagePrefixExcludedFromClear}-content-calendar-suggestion-ai-generated-toggle`,
			skipPersistFn: () => props.impersonationContext?.isValid,
			defaultIfEmpty: false,
		});

	const { onGeneratedContentToggleChanged, onSelectedUserIdChanged } = props;

	React.useEffect(() => {
		onGeneratedContentToggleChanged?.(showSuggestionsAiGeneratedToggleValue);
	}, [showSuggestionsAiGeneratedToggleValue, onGeneratedContentToggleChanged]);

	const [showToggleSuggestionsOffConfirmation, setShowToggleSuggestionsOffConfirmation] = React.useState(false);

	// Track if component is mounted to prevent state updates after unmount
	const isMountedRef = React.useRef(true);

	// Cleanup on unmount
	React.useEffect(() => {
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	// Content calendar filters
	const [selectedUserId, setSelectedUserId] = usePersistChangeInBrowserStorage<string | null>({
		storageType: 'localStorage',
		storageKey: `${storagePrefixExcludedFromClear}-content-calendar-user-filter`,
		skipPersistFn: () => props.impersonationContext?.isValid,
		defaultIfEmpty: null,
	});

	const [selectedStatus, setSelectedStatus] = usePersistChangeInBrowserStorage<string | null>({
		storageType: 'localStorage',
		storageKey: `${storagePrefixExcludedFromClear}-content-calendar-status-filter`,
		skipPersistFn: () => props.impersonationContext?.isValid,
		defaultIfEmpty: null,
	});

	const campaignCalendarRef = React.useRef<ICampaignCalendarComponent>(null);
	const contentCalendarTemplateBrowserRef = React.useRef<IContentCalendarTemplateBrowserComponent>();
	const lastCampaignIdForApprovalRef = React.useRef<string>(null);

	// Filter state for user modal
	const [showingSentByModal, setShowingSentByModal] = React.useState(false);
	const [usersWithContent, setUsersWithContent] = React.useState<{ id: string; name: string }[]>([]);
	const selectedUserQuery = useUserQuery({
		enabled: selectedUserId != null,
		id: selectedUserId,
		impersonationContext: props.impersonationContext?.toJs(),
	});

	React.useEffect(() => {
		onSelectedUserIdChanged?.(selectedUserId);
	}, [onSelectedUserIdChanged, selectedUserId]);

	// Callback to receive users from CampaignCalendar when campaign data loads
	const onCampaignDataLoaded = React.useCallback((users: { id: string; name: string }[]) => {
		setUsersWithContent(users);
	}, []);

	// Filter options
	const userFilterOptions: ISelectBoxOption<UserFilterOptions | string>[] = React.useMemo(() => {
		const isAdminMode = !!props.impersonationContext?.isValid;

		const options: ISelectBoxOption<UserFilterOptions | string>[] = [
			{
				title: 'Show all users',
				value: UserFilterOptions.All,
			},
		];

		// Add "Just Me" option only if not in admin mode
		if (!isAdminMode) {
			options.push({
				title: 'Just Me',
				value: UserFilterOptions.CurrentUser,
			});
		}

		// Add individual users if admin and we have users with content
		if (userSession.isAdmin && usersWithContent.length > 0) {
			usersWithContent.forEach(user => {
				options.push({
					title: user.name,
					value: user.id,
				});
			});
		} else if (userSession.isAdmin) {
			// Fallback to modal approach if no users extracted yet
			options.push({
				title: 'Select an Employee',
				value: UserFilterOptions.User,
			});
		}

		return options;
	}, [userSession.isAdmin, usersWithContent, props.impersonationContext?.isValid]);

	// Calculate selected user option with proper fallback handling
	const selectedUserOption = React.useMemo(() => {
		if (selectedUserId === null) {
			return userFilterOptions.find(x => x.value === UserFilterOptions.All);
		}

		if (selectedUserId === userSession.user.id) {
			// Check if CurrentUser option exists (not in admin mode)
			const currentUserOption = userFilterOptions.find(x => x.value === UserFilterOptions.CurrentUser);
			if (currentUserOption) {
				return currentUserOption;
			}
			// In admin mode, look for the specific user option
			return userFilterOptions.find(x => x.value === selectedUserId);
		}

		// For other users, try to find specific user option, fallback to generic User option
		return (
			userFilterOptions.find(x => x.value === selectedUserId) ||
			userFilterOptions.find(x => x.value === UserFilterOptions.User)
		);
	}, [selectedUserId, userFilterOptions, userSession.user.id]);

	const statusFilterOptions: ISelectBoxOption<StatusFilterOptions>[] = [
		{
			title: 'Show all statuses',
			value: StatusFilterOptions.All,
		},
		{
			title: 'Pending',
			value: StatusFilterOptions.Pending,
		},
		{
			title: 'Scheduled',
			value: StatusFilterOptions.Scheduled,
		},
		{
			title: 'Sent',
			value: StatusFilterOptions.Sent,
		},
		{
			title: 'Cancelled',
			value: StatusFilterOptions.Cancelled,
		},
	];

	// Filter handlers
	const onUserFilterChanged = (selection: ISelectBoxOption<UserFilterOptions | string>) => {
		if (selection.value === UserFilterOptions.User) {
			setShowingSentByModal(true);
			return;
		}
		if (selection.value === UserFilterOptions.CurrentUser) {
			setSelectedUserId(userSession.user.id);
			return;
		}
		if (selection.value === UserFilterOptions.All) {
			setSelectedUserId(null);
			return;
		}
		// If it's a user ID string, set it directly
		if (typeof selection.value === 'string') {
			setSelectedUserId(selection.value);
			return;
		}
		setSelectedUserId(null);
	};

	const onStatusFilterChanged = (selection: ISelectBoxOption<StatusFilterOptions>) => {
		setSelectedStatus(selection.value === StatusFilterOptions.All ? null : selection.value);
	};

	// Filter render functions
	const renderSelectedUserTitle = (option: ISelectBoxOption<UserFilterOptions | string>) => {
		if (option.value === UserFilterOptions.All) {
			return <span className={css(styleSheet.noEventSelected)}>Show all users</span>;
		}
		if (option.value === UserFilterOptions.CurrentUser) {
			return <span>Just Me</span>;
		}

		// For specific user IDs, try to find the user name in our users list
		if (typeof option.value === 'string' && option.value !== UserFilterOptions.All) {
			const user = usersWithContent.find(u => u.id === option.value);
			if (user) {
				return <span>{user.name}</span>;
			}
		}

		// Fallback to query data or option title
		let name = '';
		if (selectedUserQuery.data) {
			name =
				selectedUserQuery.data?.id === userSession.user.id
					? `${userSession.user.firstName} ${userSession.user.lastName}`
					: getDisplayName(selectedUserQuery.data);
		} else if (selectedUserQuery.error) {
			// Handle query error gracefully
			name = 'Error loading user';
		}
		const text = name ? name : option.title || 'Select an Employee';
		return <span>{text as React.ReactNode}</span>;
	};

	const renderSelectedStatusTitle = (option: ISelectBoxOption<StatusFilterOptions>) => {
		if (option.value === StatusFilterOptions.All) {
			return <span className={css(styleSheet.noEventSelected)}>Show all statuses</span>;
		}
		return <span>{option.title as React.ReactNode}</span>;
	};

	const emailContentCalPendingApprovalQuery = useEmailContentCalPendingApprovalQuery({
		enabled: !isComplianceEnabled && !props.impersonationContext,
	});
	const socialContentCalPendingApprovalQuery = useSocialContentCalPendingApprovalQuery({
		enabled: !isComplianceEnabled && isSocialMediaEnabled && !props.impersonationContext,
	});
	const blogContentCalPendingApprovalQuery = useBlogContentCalPendingApprovalQuery({
		enabled: !isComplianceEnabled && isBlogsEnabled && !props.impersonationContext,
	});

	const emailsPendingApproval = emailContentCalPendingApprovalQuery.data ?? 0;
	const socialsPendingApproval = socialContentCalPendingApprovalQuery.data ?? 0;
	const blogsPendingApproval = blogContentCalPendingApprovalQuery.data ?? 0;
	const totalPendingApprovals = emailsPendingApproval + socialsPendingApproval + blogsPendingApproval;

	// Calculate number of content types with pending approvals
	const contentTypesWithPendingApprovals = [
		emailsPendingApproval > 0,
		socialsPendingApproval > 0,
		blogsPendingApproval > 0,
	].filter(Boolean).length;

	// Only show type-specific approval links when multiple content types have pending approvals
	const shouldShowTypeApprovalLinks = contentTypesWithPendingApprovals > 1;

	const emailContentCalApproveMutation = useEmailContentCalApproveMutation();
	const socialContentCalApproveMutation = useSocialContentCalApproveMutation();
	const blogContentCalApproveMutation = useBlogContentCalApproveMutation();
	const industryOptions = [
		Api.Industry.Insurance,
		Api.Industry.RealEstate,
		Api.Industry.Financial,
		Api.Industry.Mortgage,
		Api.Industry.Legal,
		Api.Industry.Accounting,
		Api.Industry.NonProfit,
		Api.Industry.HomeMaintenance,
		Api.Industry.Others,
		Api.Industry.Health,
		Api.Industry.Dental,
		Api.Industry.Chiropractic,
	];

	const [selectedIndustries, setSelectedIndustries] = React.useState<Api.Industry[]>([
		props.impersonationContext?.account?.additionalInfo?.industry ?? userSession.account?.additionalInfo?.industry,
	]);

	const account = props.impersonationContext?.account ?? userSession.account;
	const accountIndustry = account.additionalInfo?.industry;
	const effectiveIndustries =
		selectedIndustries.length > 0 ? selectedIndustries : accountIndustry ? [accountIndustry] : [];
	const suggestionQueriesParams = Array.from({ length: numMonthsInQuarterly }, (_, i) => i).map(i => {
		const currentMonth = new Date(month);
		const suggestionMonth = new Date(currentMonth.setMonth(currentMonth.getMonth() + i));
		const param: Parameters<typeof useContentCalendarSuggestionFilterQuery>[0] = {
			enabled: Boolean(showSuggestionsToggleValue),
			month: suggestionMonth.toISOString(),
			impersonationContext: props.impersonationContext?.toJs(),
			request: {
				accountAges:
					selectedAccountAge.dataContext === Api.ContentCalendarSuggestionAccountAge.New
						? [Api.ContentCalendarSuggestionAccountAge.New]
						: [Api.ContentCalendarSuggestionAccountAge.New, Api.ContentCalendarSuggestionAccountAge.Existing],
				verticals: effectiveIndustries.map(industry => ({
					industry,
					subverticals:
						industry === accountIndustry
							? (account.preferences?.subverticalSettings?.subverticals ?? [Api.ContentCalendarSuggestionAll])
							: [Api.ContentCalendarSuggestionAll],
				})),
			},
		};
		return param;
	});

	const suggestionQueries = useContentCalendarSuggestionFilterQueries(suggestionQueriesParams);

	const suggestionsForCampaignTypes = React.useMemo(() => {
		const values = suggestionQueries
			.filter(x => x.isSuccess)
			.flatMap(x => x.data)
			.filter(x => isSuggestionInCampaignTypes(x, campaignTypes));

		return showSuggestionsAiGeneratedToggleValue ? values.filter(x => x.calendarId) : values.filter(x => !x.calendarId);
	}, [suggestionQueries, showSuggestionsAiGeneratedToggleValue, campaignTypes]);

	const allBulkSelectableSuggestions = React.useMemo(() => {
		const values = suggestionQueries
			.filter(x => x.isSuccess)
			.flatMap(x => x.data)
			.filter(x => isSuggestionInCampaignTypes(x, enabledTypes));

		return showSuggestionsAiGeneratedToggleValue ? values.filter(x => x.calendarId) : values.filter(x => !x.calendarId);
	}, [suggestionQueries, showSuggestionsAiGeneratedToggleValue, enabledTypes]);

	const suggestionQueriesErrors: string[] = React.useMemo(() => {
		const errors = suggestionQueries
			.filter(x => x.isError)
			.flatMap(x => (x.error as Api.IOperationResultNoValue)?.systemMessage);

		return Array.from(new Set(errors));
	}, [suggestionQueries]);

	const hasGeneratedCalendar = React.useMemo(() => {
		const values = suggestionQueries
			.filter(x => x.isSuccess)
			.flatMap(x => x.data)
			.filter(x => isSuggestionInCampaignTypes(x, campaignTypes));

		return values.some(x => x.calendarId);
	}, [suggestionQueries, campaignTypes]);

	const selectSingleCampaignType = (campaignType: CampaignType) => {
		setTemplateBrowserCampaignType(campaignType);
		setCampaignTypes([campaignType]);
	};
	const updateCampaignTypes = (newCampaignTypes: CampaignType[]) => {
		setCampaignTypes(newCampaignTypes);
		if (!newCampaignTypes.includes(templateBrowserCampaignType)) {
			setTemplateBrowserCampaignType(newCampaignTypes[0]);
		}
	};

	React.useEffect(() => {
		props.onSuggestionsToggleChanged?.(showSuggestionsToggleValue);
	}, [showSuggestionsToggleValue, props]);

	const suggestionsContext = useCreateCampaignCalendarSuggestionsContext({
		supportsBulkScheduleSuggestions: showSuggestionsToggleValue,
		onScheduleSuggestions: selectedSuggestions => {
			const finalCleanup = () => {
				suggestionsContext.clearSelectedSuggestions();
				loadAllCalendarData();
				fullscreenModal.dismissModal();
			};

			const emailSuggestions = selectedSuggestions.filter(s => getSuggestionCampaignType(s) === CampaignType.Email);
			const socialSuggestions = selectedSuggestions.filter(s => getSuggestionCampaignType(s) === CampaignType.Social);
			const blogSuggestions = selectedSuggestions.filter(s => getSuggestionCampaignType(s) === CampaignType.Blog);

			const steps: { type: CampaignType; suggestions: IContentCalendarSelectedSuggestion[] }[] = [];
			if (emailSuggestions.length > 0) {
				steps.push({ type: CampaignType.Email, suggestions: emailSuggestions });
			}
			if (socialSuggestions.length > 0) {
				steps.push({ type: CampaignType.Social, suggestions: socialSuggestions });
			}
			if (blogSuggestions.length > 0) {
				steps.push({ type: CampaignType.Blog, suggestions: blogSuggestions });
			}

			if (steps.length === 0) {
				return;
			}

			const getPathnameForType = (type: CampaignType) => {
				switch (type) {
					case CampaignType.Email:
						return '/multi-campaign-scheduler/email';
					case CampaignType.Social:
						return '/multi-campaign-scheduler/social';
					case CampaignType.Blog:
						return '/multi-campaign-scheduler/blogs';
					default:
						return '/multi-campaign-scheduler';
				}
			};

			const isMultiTypeSequence = steps.length > 1;

			const pushTransitionScreen = (transitionRequest: ISequenceTransitionRequest) => {
				if (!props.onOpenSequenceTransition?.(transitionRequest)) {
					fullscreenModal.history.push({
						pathname: '/multi-campaign-scheduler/transition',
						state: { model: transitionRequest } as ILocationState<any, ISequenceTransitionRequest>,
					});
				}
			};

			const pushScheduler = (step: (typeof steps)[number], remaining: typeof steps) => {
				const stepSuggestions = [...step.suggestions].sort(
					(a, b) => new Date(a.schedule.startDate).getTime() - new Date(b.schedule.startDate).getTime()
				);

				const stepOnFinish = isMultiTypeSequence
					? () => {
							pushTransitionScreen({
								completedType: step.type,
								completedCount: stepSuggestions.length,
								remainingSteps: remaining.map(r => ({
									type: r.type,
									count: r.suggestions.length,
									onSchedule: () =>
										pushScheduler(
											r,
											remaining.filter(x => x !== r)
										),
								})),
								onClose: finalCleanup,
							});
						}
					: finalCleanup;

				const sequenceFlags = {
					isSequenceStep: isMultiTypeSequence,
				};

				if (!props.onOpenMultiSuggestionsScheduler?.(step.type, stepSuggestions, stepOnFinish, sequenceFlags)) {
					const locationState: ILocationState<any, IMultiCampaignSchedulerRequest> = {
						model: {
							suggestions: stepSuggestions,
							onFinish: stepOnFinish,
							...sequenceFlags,
						},
					};

					fullscreenModal.history.push({
						pathname: getPathnameForType(step.type),
						state: locationState,
					});
				}
			};

			pushScheduler(steps[0], steps.slice(1));
		},
	});
	const selectedCountsByType = React.useMemo(() => {
		const counts: Partial<Record<CampaignType, number>> = {};
		for (const s of suggestionsContext.selectedSuggestions) {
			const type = getSuggestionCampaignType(s);
			counts[type] = (counts[type] ?? 0) + 1;
		}
		return counts;
	}, [suggestionsContext.selectedSuggestions]);

	const selectSuggestionRef = React.useRef(suggestionsContext.selectSuggestion);
	selectSuggestionRef.current = suggestionsContext.selectSuggestion;

	React.useEffect(() => {
		if (!showSuggestionsToggleValue) {
			suggestionsContext.clearSelectedSuggestions();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showSuggestionsToggleValue]);

	const loadAllCalendarData = React.useCallback(async () => {
		invalidateAllContentCalendarSuggestions();
		if (!props.impersonationContext) {
			invalidateEmailContentCalPendingApprovalQuery();
			invalidateSocialContentCalPendingApprovalQuery();
			invalidateBlogContentCalPendingApprovalQuery();
		}
		if (campaignCalendarRef.current) {
			await campaignCalendarRef.current.loadCampaigns();
			// After campaigns load, trigger user extraction
			// We'll implement this by listening to campaign data changes
		}
	}, [props.impersonationContext]);

	const launchReviewAndApproveSocial = async (campaign: Api.SocialMediaPostReportViewModel) => {
		lastCampaignIdForApprovalRef.current = campaign.id;
		try {
			const post = await campaign?.load();

			// Check if component is still mounted after async operation
			if (!isMountedRef.current) {
				return;
			}

			const locationState: ILocationState<Api.SocialMediaPostReportViewModel, any> = {
				viewModel: new Api.SocialMediaPostReportViewModel(userSession, post),
			};
			fullscreenModal?.history?.push({
				pathname: '/social-media/post/approval-preview',
				state: locationState,
			});
		} catch (error) {
			// Check if component is still mounted before updating error state
			if (isMountedRef.current) {
				logApiError('LaunchReviewAndApproveSocial-Error', error);
				errorMessages.pushApiError(error);
			}
		}
	};
	const loadBlogPostAsync = async (blogId: string) => {
		return getBlogPostAsync({ id: blogId, impersonationContext: props.impersonationContext, userSession });
	};
	const launchEditBlogPost = (post: Api.IBlogPost, schedule?: Api.IScheduledSend) => {
		const sendFromUser = selectedUserId && selectedUserQuery.data ? selectedUserQuery.data : userSession.user;
		const locationState: ILocationState<any, BlogPostRequest> = {
			model: {
				post,
				schedule,
				sendFromUser,
				impersonationContext: props.impersonationContext?.toJs(),
			},
		};
		// Construct the correct pathname based on whether we're in admin mode
		const pathname = props.impersonationContext?.account?.id
			? `/accounts/${props.impersonationContext.account.id}/content-calendar/blog/post/edit/${post.id}`
			: `/blog/post/edit/${post.id}`;
		fullscreenModal?.history?.push({
			pathname,
			state: locationState,
		});
	};
	const loadSocialPostAndLaunch = async (postId: string, schedule?: Api.IScheduledSend, initialCreatorId?: string) => {
		try {
			// Create a minimal social post object with just the ID
			const minimalPost: Partial<Api.ISocialMediaPost> = { id: postId };
			const baseImpersonationContext = props.impersonationContext?.account?.id
				? props.impersonationContext.toJs()
				: undefined;
			const initialImpersonationUserId = initialCreatorId || selectedUserId || baseImpersonationContext?.user?.id;
			const initialImpersonationContext =
				baseImpersonationContext && initialImpersonationUserId
					? {
							...baseImpersonationContext,
							user: { id: initialImpersonationUserId },
						}
					: baseImpersonationContext;

			// Create the viewModel and load the full post data
			const postVm = new Api.SocialMediaPostViewModel(userSession, minimalPost);
			if (initialImpersonationContext) {
				postVm.impersonate(initialImpersonationContext);
			}
			await postVm.load();

			const finalImpersonationUserId = postVm.creator?.id || initialImpersonationUserId;
			const impersonationContext =
				baseImpersonationContext && finalImpersonationUserId
					? {
							...baseImpersonationContext,
							user: { id: finalImpersonationUserId },
						}
					: baseImpersonationContext;
			if (impersonationContext) {
				postVm.impersonate(impersonationContext);
			}

			const sendFromUser =
				postVm.creator || (selectedUserId && selectedUserQuery.data ? selectedUserQuery.data : userSession.user);

			// Include schedule in the location state if provided
			const locationState: ILocationState<Api.SocialMediaPostViewModel, any> = {
				viewModel: postVm,
				model: {
					...(schedule ? { schedule } : {}),
					sendFromUser,
					impersonationContext,
				},
			};

			fullscreenModal?.history?.push({
				pathname: props.impersonationContext?.account?.id
					? `/accounts/${props.impersonationContext.account.id}/content-calendar/post/create`
					: `/social-media/post/edit-post-draft/${postVm.id}`,
				state: locationState,
			});
		} catch (error) {
			errorMessages.pushApiError(error);
			logApiError('LoadSocialPost-Error', error);
			throw error;
		}
	};
	React.useEffect(() => {
		if (props.impersonationContext?.account && props.impersonationContext?.user) {
			loadAllCalendarData();
		}
	}, [loadAllCalendarData, props.impersonationContext?.account, props.impersonationContext?.user]);

	React.useEffect(() => {
		const initialize = async () => {
			const campaignApprovalCommand = location.state;
			if (campaignApprovalCommand) {
				switch (campaignApprovalCommand.command) {
					case 'social-approval': {
						const vm = new Api.SocialMediaPostReportViewModel(userSession, {
							id: campaignApprovalCommand.id,
						}).impersonate(props.impersonationContext);
						launchReviewAndApproveSocial(vm);
						break;
					}
					case 'blog-approval': {
						try {
							const blogPost = await loadBlogPostAsync(campaignApprovalCommand.id);

							// Check if component is still mounted after async operation
							if (!isMountedRef.current) {
								return;
							}

							launchEditBlogPost(blogPost);
						} catch (error) {
							// Check if component is still mounted before logging error
							if (isMountedRef.current) {
								logApiError('BlogPostLoad-Error', error);
							}
						}
						break;
					}
					default: {
						break;
					}
				}
			}
		};
		initialize();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		// sets suggestions to show email only when the toggle is turned on and more than one campaign type is selected
		if (showSuggestionsToggleValue && campaignTypes.length > 1) {
			setCampaignTypes([CampaignType.Email]);
		}
	}, [campaignTypes.length, setCampaignTypes, showSuggestionsToggleValue]);

	const loadTemplate = (templateId: string) => {
		try {
			return new Api.TemplatesViewModel(userSession)
				.impersonate(props.impersonationContext)
				.getByIdExpandedByLastUsedBy(templateId);
		} catch (error) {
			errorMessages.pushApiError(error);
			logApiError('LoadTemplate-Error', error);
		}
	};
	const openCampaignComposer = (
		template: Api.ITemplate,
		schedule?: Api.IScheduledSend,
		referrer = 'contentCalendar'
	) => {
		if (!props.onOpenCampaignComposer?.(template, schedule, referrer)) {
			if (template?.templateType === Api.TemplateType.Email || !template?.templateType) {
				const sendFromUser = selectedUserId && selectedUserQuery.data ? selectedUserQuery.data : userSession.user;
				const locationStateEmail: ILocationState<any, ICreateCampaignRequest<TemplateOrTemplateFilter>> = {
					model: {
						context: template,
						onFinish: (didSend: boolean) => {
							if (didSend) {
								loadAllCalendarData();
							}
						},
						schedule,
						sendFromUser,
						type: 'Template',
					},
				};
				fullscreenModal?.history.push({
					pathname: '/email/campaigns/create/from-template',
					state: locationStateEmail,
				});
			} else if (template.templateType === Api.TemplateType.Blog) {
				const sendFromUser = selectedUserId && selectedUserQuery.data ? selectedUserQuery.data : userSession.user;
				const locationState: ILocationState<any, BlogPostRequest> = {
					model: {
						schedule,
						sendFromUser,
						template,
					},
				};
				fullscreenModal?.history.push({
					pathname: `/blog/post/create-from-template/${template.id}`,
					state: locationState,
				});
			} else if (template.templateType === Api.TemplateType.SocialMediaPost) {
				const sendFromUser = selectedUserId && selectedUserQuery.data ? selectedUserQuery.data : userSession.user;
				const locationStateSocial: ILocationState<any, ICreateSocialMediaPostRequest<Api.ITemplate>> = {
					model: {
						context: template,
						onFinish: (didSend: boolean) => {
							if (didSend) {
								loadAllCalendarData();
							}
						},
						schedule,
						sendFromUser,
						type: 'Template',
					},
				};
				fullscreenModal?.history.push({
					pathname: `/social-media/post/create/from-template`,
					state: locationStateSocial,
				});
			}
		}
	};

	const createSuggestionFromDropResult = async (result: DropResult) => {
		if (result.destination?.droppableId && result.destination?.droppableId !== result.source?.droppableId) {
			const templateId = result.draggableId;

			// Try to get template card from drag data first (preferred method)
			let templateCard: ITemplateCard | undefined = (result as any).source?.data?.templateCard;

			// Fallback to ref lookup if drag data is not available (for backward compatibility)
			if (!templateCard) {
				const allTemplateCards = contentCalendarTemplateBrowserRef.current?.getTemplateCards?.();
				templateCard = allTemplateCards?.[result.source.index];
			}
			if (templateCard) {
				try {
					// For blog drafts, the template may not be fetchable via the standard endpoint
					// (e.g., industry templates on admin). Use card data directly to avoid 404s.
					const isBlogDraft = templateCard.isDraft && templateBrowserCampaignType === CampaignType.Blog;
					let template: Api.ITemplate | null = null;

					if (!isBlogDraft) {
						template = await loadTemplate(templateId);

						// Check if component is still mounted after async operation
						if (!isMountedRef.current) {
							return;
						}
					}

					let startDateAsString = result.destination.droppableId;
					/**
					 * @NOTE If the start date is before today and the calendar is quarterly, set the start date to today.
					 * This is to prevent users from scheduling suggestions in the past.
					 */
					const startDateMoment = moment(startDateAsString);
					if (startDateMoment.isBefore(moment().startOf('day')) && calendarType === CalendarType.Quarterly) {
						startDateAsString = moment().toISOString();
					}

					const templateScope = template?.scope ?? templateCard.scope;
					const suggestion: IContentCalendarSelectedSuggestion & { _cachedTemplateCard?: ITemplateCard } = {
						dragDropId: uuidgen(), // need this because we dont have a suggestion id
						schedule: {
							startDate: startDateAsString,
							expirationDate: templateCard?.schedule?.expirationDate,
						},
						templateReference: {
							isCustomized: false,
							isSystemTemplate: [Api.TemplateScope.System, Api.TemplateScope.Industry].some(x => x === templateScope),
							name: template?.name ?? templateCard.name,
							templateId,
							templateType: template?.templateType ?? Api.TemplateType.Blog,
						},
						// For blog drafts, cache the card data so the multi-scheduler
						// can render without re-fetching (avoids 404 on cross-account templates)
						_cachedTemplateCard: isBlogDraft ? templateCard : undefined,
					};

					const hasAlreadySelectedSameSuggestion = suggestionsContext.selectedSuggestions.find(selectedSuggestion => {
						return selectedSuggestion.templateReference.templateId === suggestion.templateReference.templateId;
					});
					const shouldShowDuplicate = template ? shouldShowDuplicateTemplateWarning(template) : false;
					const showDuplicateWarning = hasAlreadySelectedSameSuggestion || shouldShowDuplicate;
					if (showDuplicateWarning) {
						setDuplicateTemplateDialogProps(modalPropsBeforeOpen => ({
							...modalPropsBeforeOpen,
							isOpen: true,
							onRequestClose: (suppress?: boolean, cancel = false) => {
								setDuplicateTemplateDialogProps(value => ({ ...value, isOpen: false }));
								if (suppress) {
									suppressDuplicateTemplateWarning(template?.id ?? templateId);
								}
								if (!cancel && selectSuggestionRef.current) {
									selectSuggestionRef.current(suggestion);
									toast.success('Suggestion added to selection');
								}
							},
							type: campaignTypes[0], // should only be 1 selected
							duplicateType: template ? getDuplicateTemplateType(template) : undefined,
							lastUsedDate: template?.lastUsedByDate?.account ?? template?.lastUsedByDate?.currentUser,
						}));
						return;
					}
					suggestionsContext.selectSuggestion(suggestion);
				} catch (error) {
					// Check if component is still mounted before updating error state
					if (isMountedRef.current) {
						errorMessages.pushApiError(error);
						logApiError('CreateSuggestionFromDropResult-Error', error);
					}
				}
			}
		}
	};

	const onDragEndInternal = async (result: DropResult) => {
		if (showSuggestionsToggleValue) {
			return createSuggestionFromDropResult(result);
		}

		if (result.destination?.droppableId && result.destination?.droppableId !== result.source?.droppableId) {
			const templateId = result.draggableId;

			// Try to get template card from drag data first (preferred method)
			let templateCard: ITemplateCard | undefined = (result as any).source?.data?.templateCard;

			// Fallback to ref lookup if drag data is not available (for backward compatibility)
			if (!templateCard) {
				templateCard = contentCalendarTemplateBrowserRef.current?.getTemplateCards?.()?.[result.source.index];
			}
			if (templateCard) {
				setPlaceholders([
					{
						campaignType: templateBrowserCampaignType,
						dataContext: templateCard,
						moment: moment(result.destination.droppableId),
						status:
							templateBrowserCampaignType === CampaignType.Email
								? Api.EmailSendStatus.Queued
								: templateBrowserCampaignType === CampaignType.Social
									? Api.PostStatus.Scheduled
									: Api.BlogStatus.Queued,
						subject: templateCard.name,
					},
				]);
			}
			try {
				// Handle blog and social drafts differently from templates
				if (templateCard?.isDraft && templateBrowserCampaignType === CampaignType.Blog) {
					const blogPost = await loadBlogPostAsync(templateId);

					// Check if component is still mounted after async operation
					if (!isMountedRef.current) {
						return;
					}

					launchEditBlogPost(blogPost, {
						startDate: result.destination?.droppableId,
					});
				} else if (templateCard?.isDraft && templateBrowserCampaignType === CampaignType.Social) {
					await loadSocialPostAndLaunch(
						templateId,
						{
							startDate: result.destination?.droppableId,
						},
						templateCard?.creator?.id
					);

					// Check if component is still mounted after async operation
					if (!isMountedRef.current) {
						return;
					}
				} else {
					const template = await loadTemplate(templateId);

					// Check if component is still mounted after async operation
					if (!isMountedRef.current) {
						return;
					}

					openCampaignComposer(template, {
						startDate: result.destination?.droppableId,
					});
				}
			} catch {
				setPlaceholders([]);
			}
		}
	};

	const onDragEnd = props.onDragEnd || onDragEndInternal;

	// Monitor pragmatic-drag-and-drop events and convert them to DropResult format
	React.useEffect(() => {
		const cleanup = monitorForElements({
			onDrop({ source, location: pragmaticLocation }) {
				// Only handle drops that are from template cards to calendar
				if (source.data.type === droppableType && pragmaticLocation.current.dropTargets.length > 0) {
					const destination = pragmaticLocation.current.dropTargets[0];

					// Construct a DropResult-like object that matches react-beautiful-dnd format
					const result: ExtendedDropResult = {
						draggableId: source.data.id as string,
						type: source.data.type as string,
						source: {
							droppableId: 'campaignTemplatesList', // This is the app drawer template list
							index: source.data.index as number,
							// Embed the template card data in the source for easy access
							data: source.data,
						},
						destination: {
							droppableId: destination.data.id as string,
							index: 0, // Calendar drops don't have a specific index
						},
						reason: 'DROP',
					};

					// Call the same onDragEnd handler
					onDragEnd(result);
				}
			},
		});

		return cleanup;
	}, [onDragEnd]);

	React.useImperativeHandle(
		ref,
		() => ({
			loadAllCalendarData,
			onDragEndInternal,
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[month, onDragEndInternal]
	);

	const onApproveEmailCampaigns = async () => {
		logEvent('ApproveEmailCampaigns');
		try {
			await emailContentCalApproveMutation.mutateAsync({
				impersonationContext: props.impersonationContext,
			});
			loadAllCalendarData();
		} catch (error) {
			logApiError('ApproveEmailCampaigns-Error', error);
		}
	};

	const onApproveSocialCampaigns = async () => {
		logEvent('ApproveSocialCampaigns');
		try {
			await socialContentCalApproveMutation.mutateAsync({
				impersonationContext: props.impersonationContext,
			});
			loadAllCalendarData();
		} catch (error) {
			logApiError('ApproveSocialCampaigns-Error', error);
		}
	};

	const onApproveBlogCampaigns = async () => {
		logEvent('ApproveBlogCampaigns');
		try {
			await blogContentCalApproveMutation.mutateAsync({
				impersonationContext: props.impersonationContext,
			});
			loadAllCalendarData();
		} catch (error) {
			logApiError('ApproveBlogCampaigns-Error', error);
		}
	};

	const onApprovePendingApprovals = async () => {
		logEvent('ApprovePendingApprovals');
		try {
			await emailContentCalApproveMutation.mutateAsync({
				impersonationContext: props.impersonationContext,
			});

			if (isSocialMediaEnabled) {
				await socialContentCalApproveMutation.mutateAsync({
					impersonationContext: props.impersonationContext,
				});
			}

			if (isBlogsEnabled) {
				await blogContentCalApproveMutation.mutateAsync({
					impersonationContext: props.impersonationContext,
				});
			}
			loadAllCalendarData();
		} catch (error) {
			logApiError('ApprovePendingApprovals-Error', error);
		}
	};
	const confirmToggle = () => {
		setShowSuggestionsToggleValue(!showSuggestionsToggleValue);
		suggestionsContext.clearSelectedSuggestions();
		setShowToggleSuggestionsOffConfirmation(false);
	};

	const toggle = () => {
		const isAdmin = !!props.impersonationContext?.account?.id;

		if (!isAdmin) {
			if (suggestionsContext.selectedSuggestions.length > 0) {
				setShowToggleSuggestionsOffConfirmation(true);
			} else {
				confirmToggle();
			}
		} else {
			if (showSuggestionsToggleValue) {
				setShowSuggestionsToggleValue(false);
				return;
			}

			if (suggestionsContext.selectedSuggestions.length > 0) {
				setShowToggleSuggestionsOffConfirmation(true);
			} else {
				setIsAiGeneratedContentSelectionModalOpen(true);
			}
		}
	};
	const onCancelConfirmClose = (_result?: IConfirmationDialogOption, canceled?: boolean) => {
		if (!canceled) {
			confirmToggle();
		}
		setShowToggleSuggestionsOffConfirmation(false);
	};
	const launchReviewAndApprove = (campaign: Api.CampaignViewModel) => {
		lastCampaignIdForApprovalRef.current = campaign.id;
		const locationState: ILocationState<CampaignsApprovalViewModel, any> =
			campaign instanceof Api.CampaignViewModel
				? {
						viewModel: new CampaignsApprovalViewModel(userSession, [campaign]),
					}
				: null;
		fullscreenModal?.history?.push({
			pathname: '/email/campaigns/edit',
			state: locationState,
		});
	};
	const onCampaignClicked = async (e: React.MouseEvent<HTMLElement>, campaign: Api.CampaignViewModel) => {
		props.onCampaignClicked?.(e, campaign);
		if (!e.defaultPrevented && campaign.status === Api.EmailSendStatus.Queued) {
			e.preventDefault();
			const locationState: ILocationState<Api.CampaignViewModel, any> = {
				viewModel: campaign,
			};
			fullscreenModal?.history?.push({
				pathname: '/email/campaigns/edit',
				state: locationState,
			});
		}
	};
	const onBlogPostClicked = async (e: React.MouseEvent<HTMLElement>, blog: Api.IBlogReportView | IBlogTemplateCard) => {
		props.onBlogPostClicked?.(e, blog);
		if (!e.defaultPrevented && blog.id) {
			e.preventDefault();
			try {
				const result = await loadBlogPostAsync(blog.id);

				// Check if component is still mounted after async operation
				if (!isMountedRef.current) {
					return;
				}

				launchEditBlogPost(result);
			} catch (error) {
				// Check if component is still mounted before updating error state
				if (isMountedRef.current) {
					errorMessages.pushApiError(error);
				}
			}
		}
	};
	const onMonthsChange = (value: moment.Moment) => {
		const newMonth = value.toDate();
		setMonth(newMonth);
	};
	const onCalendarRef = (calRef: ICampaignCalendarComponent) => {
		campaignCalendarRef.current = calRef;
		props.onCampaignCalendarRef?.(calRef);
	};
	const onSocialMediaEventClicked = async (
		e: React.MouseEvent<HTMLElement, MouseEvent>,
		postReport: Api.SocialMediaPostReportViewModel
	) => {
		props.onSocialMediaPostClicked?.(e, postReport);
		if (e.defaultPrevented) {
			return;
		}
		e.preventDefault();
		e?.stopPropagation();
		const locationState: ILocationState<Api.SocialMediaPostReportViewModel, any> = { viewModel: postReport };

		if (postReport?.status === Api.PostStatus.Pending) {
			fullscreenModal?.history.push({
				pathname: '/social-media/post/approval-preview',
				state: locationState,
			});
		} else {
			fullscreenModal?.history.push({
				pathname: `/social-media/post/edit-from-post-report`,
				state: locationState,
			});
		}
	};

	const onSuggestedEventClicked = async (
		e: React.MouseEvent<HTMLElement, MouseEvent>,
		suggestion: Api.IContentCalendarSuggestion
	) => {
		props.onSuggestionClicked?.(e, suggestion);
		if (e.defaultPrevented) {
			return;
		}
		e.preventDefault();
		if (suggestionsContext.supportsBulkScheduleSuggestions && suggestionsContext.hasSelectedSuggestion(suggestion)) {
			toast.error(
				'To schedule, please schedule all suggestions at once using the "Schedule suggested content" button.'
			);
			return;
		}

		if (suggestion.templateReference.templateType === Api.TemplateType.Blog) {
			try {
				const result = await getBlogPostTemplateAsync({
					id: suggestion.templateReference.templateId,
					userSession,
					impersonationContext: props.impersonationContext?.toJs(),
				});
				if (!result.contentCalendarSuggestionId) {
					result.contentCalendarSuggestionId = suggestion.id;
				}
				const locationState: ILocationState<any, BlogPostRequest> = {
					model: {
						impersonationContext: props.impersonationContext?.toJs(),
						template: result,
						schedule: suggestion.schedule,
					},
				};
				fullscreenModal?.history?.push({
					pathname: `/blog/post/create-from-template/${result.id}`,
					state: locationState,
				});
			} catch (error) {
				logApiError('LoadSuggestion-Error', error);
				errorMessages.pushApiError(error);
			}
			return;
		}

		const viewModel = new Api.ContentCalenderSuggestionViewModel(userSession, suggestion).impersonate(
			props.impersonationContext
		);
		try {
			await viewModel.load();
			const locationState: ILocationState<Api.ContentCalenderSuggestionViewModel, any> = { viewModel };
			fullscreenModal?.history?.push({
				pathname: '/email/campaigns/create/from-suggested-campaign',
				state: locationState,
			});
		} catch (error) {
			errorMessages.pushApiError(error);
			logApiError('LoadSuggestion-Error', error);
		}
	};

	const onSuggestedSocialMediaEventClicked = async (
		e: React.MouseEvent<HTMLElement>,
		suggestion: Api.IContentCalendarSuggestion
	) => {
		props.onSuggestedSocialMediaEventClicked?.(e, suggestion);
		if (e.defaultPrevented) {
			return;
		}
		e.preventDefault();

		if (suggestionsContext.supportsBulkScheduleSuggestions && suggestionsContext.hasSelectedSuggestion(suggestion)) {
			toast.error(
				'To schedule, please schedule all suggestions at once using the "Schedule suggested content" button.'
			);
			return;
		}

		try {
			const locationState: ILocationState<any, ICreateSocialMediaPostRequest<Api.ITemplate | string>> = {
				model: {
					context: suggestion?.templateReference?.templateId,
					onFinish: (didSend: boolean) => {
						if (didSend) {
							loadAllCalendarData();
						}
					},
					schedule: suggestion.schedule,
					targets: suggestion.targets,
					type: 'Template',
				},
			};
			fullscreenModal?.history.push({
				pathname: `/social-media/post/edit-template/${suggestion.templateReference.templateId}/${suggestion.id}`,
				state: locationState,
			});
		} catch (error) {
			errorMessages.pushApiError(error);
			logApiError('LoadSuggestion-Error', error);
		}
	};
	const onFullscreenModalLifecycleNotification = (notification: ILocalNotification<string>) => {
		if (notification.info === 'onAfterClose') {
			setPlaceholders([]);
		}
	};
	const onEditCampaignNotificationReceived = async (notification?: ILocalNotification<Api.ICampaign[]>) => {
		if (
			notification?.info?.some(
				x => x.id === lastCampaignIdForApprovalRef.current && x.status !== Api.EmailSendStatus.WaitingForApproval
			) ||
			notification?.info.length === 0 ||
			notification?.info[0]?.status === Api.EmailSendStatus.Cancelled
		) {
			// reload if the status has changed
			loadAllCalendarData();
			lastCampaignIdForApprovalRef.current = null;
		}
	};
	const onRefreshFromNotification = async (notification?: ILocalNotification<any>) => {
		if (notification?.info) {
			loadAllCalendarData();
		}
	};
	const onEditSocialNotificationReceived = async (notification?: ILocalNotification<Api.SocialMediaPostViewModel>) => {
		if (notification?.info) {
			// reload if the status has changed
			loadAllCalendarData();
		}
	};
	const onEditBlogPost = (notification?: ILocalNotification<Api.IBlogPost>) => {
		if (notification?.info) {
			// reload if the status has changed
			loadAllCalendarData();
		}
	};

	// order matters
	const computedCalendarContainerCardStyles = [
		styleSheet.calendarContainerCard,
		showSuggestionsToggleValue && calendarType === CalendarType.Quarterly
			? styleSheet.calendarContainerCardWithSuggestions
			: null,
		totalPendingApprovals > 0 &&
			calendarType === CalendarType.Quarterly &&
			(showSuggestionsToggleValue
				? styleSheet.calendarContainerCardWithAlertAndSuggestions
				: styleSheet.calendarContainerCardWithAlert),
	];
	let monthsToShow: number;
	switch (calendarType) {
		case CalendarType.Month:
			monthsToShow = 1;
			break;
		case CalendarType.Weekly:
			monthsToShow = 0;
			break;
		case CalendarType.Quarterly:
		default:
			monthsToShow = numMonthsInQuarterly;
			break;
	}

	const appDrawerContext = useAppDrawerContext();

	return (
		<div className={`${css(styleSheet.container, ...(props.styles || []))} content-calendar ${props.className || ''}`}>
			<MultiContainerHeader appBarHeader={<h1 className={css(bs.breadcrumbTitle)}>Content Calendar</h1>} />

			<FabContext appearance={{ hidden: true }} />
			<CampaignCalendarSuggestionsContext.Provider value={suggestionsContext}>
				<div className={css(styleSheet.contentContainer)}>
					<DragDropContext onDragEnd={onDragEnd}>
						<>
							<div className={css(styleSheet.calendarContainer)}>
								{totalPendingApprovals > 0 ? (
									<div className={css(styleSheet.pendingApprovalAlert)}>
										<figure>
											<ApproveAllIcon />
										</figure>
										<span className={css(bs.uppercase, styleSheet.pendingApprovalAlertText)}>
											<strong className={css(styleSheet.pendingApprovalText)}>Pending Approval</strong>
										</span>

										<div className={css(styleSheet.pendingApprovalCounts)}>
											{emailsPendingApproval > 0 ? (
												<div className={css(styleSheet.checkboxContent)}>
													<span
														className={css(styleSheet.calendarCardTypeColor, styleSheet.calendarCardTypeColorEmail)}
													/>
													<span className={css(styleSheet.showLabel)}>
														{`${emailsPendingApproval} Email Campaigns`}
														{shouldShowTypeApprovalLinks && (
															<span
																className={css(styleSheet.pendingApprovalTypeLink)}
																onClick={onApproveEmailCampaigns}
															>
																{' '}
																(Approve)
															</span>
														)}
													</span>
												</div>
											) : null}
											{socialsPendingApproval > 0 ? (
												<div className={css(styleSheet.checkboxContent)}>
													<span
														className={css(styleSheet.calendarCardTypeColor, styleSheet.calendarCardTypeColorSocial)}
													/>
													<span className={css(styleSheet.showLabel)}>
														{`${socialsPendingApproval} Social Media Posts`}
														{shouldShowTypeApprovalLinks && (
															<span
																className={css(styleSheet.pendingApprovalTypeLink)}
																onClick={onApproveSocialCampaigns}
															>
																{' '}
																(Approve)
															</span>
														)}
													</span>
												</div>
											) : null}
											{blogsPendingApproval > 0 ? (
												<div className={css(styleSheet.checkboxContent)}>
													<span
														className={css(styleSheet.calendarCardTypeColor, styleSheet.calendarCardTypeColorBlog)}
													/>
													<span className={css(styleSheet.showLabel)}>
														{`${blogsPendingApproval} Blog Posts`}
														{shouldShowTypeApprovalLinks && (
															<span
																className={css(styleSheet.pendingApprovalTypeLink)}
																onClick={onApproveBlogCampaigns}
															>
																{' '}
																(Approve)
															</span>
														)}
													</span>
												</div>
											) : null}
										</div>

										<div className={css(styleSheet.pendingApprovalAlertButtonContainer)}>
											<button
												className={css(styleSheet.pendingApprovalAlertButton)}
												onClick={onApprovePendingApprovals}
											>
												<span>Approve All</span>
											</button>
										</div>
									</div>
								) : null}

								{suggestionQueriesErrors.length > 0 && (
									<div className={css(styleSheet.loadingSuggestionsAlert)}>
										<figure>
											<CancelIconFill />
										</figure>

										<div className={css(styleSheet.pendingApprovalCounts)}>
											{suggestionQueriesErrors.map(error => (
												<div className={css(styleSheet.checkboxContent)} key={error}>
													<span>{error}</span>
												</div>
											))}
										</div>
									</div>
								)}

								{month ? (
									<div className={css(bs.horizontalStack, styleSheet.suggestionsToggleContainer)}>
										{!showSuggestionsToggleValue ? (
											<div className={css(styleSheet.typesKey)}>
												<label className={css(styleSheet.selectLabel, styleSheet.campaignTypeLabel)}>Campaigns</label>
												<ContentCalendarLegend
													campaignTypes={campaignTypes}
													onSetCampaignTypes={updateCampaignTypes}
													rightSection={
														<div className={css(styleSheet.suggestionToggleContainer)}>
															<div className={css(styleSheet.suggestionToggleWrap)}>
																<Toggle
																	checkedColor={THEME.colors.primary.main}
																	id='show-suggestions-toggle'
																	isOn={showSuggestionsToggleValue}
																	onToggleCheckChanged={toggle}
																	text={!showSuggestionsToggleValue ? 'Turn on suggestions!' : 'Turn off suggestions'}
																	textStyles={[styleSheet.suggestionsToggleText]}
																	uncheckedColor={THEME.colors.border.light}
																/>
															</div>
														</div>
													}
												/>
											</div>
										) : (
											<ContentCalendarTabs
												campaignTypes={enabledTypes}
												activeCampaignType={campaignTypes?.[0]}
												onSetCampaignActiveCampaignType={selectSingleCampaignType}
												selectedCountsByType={selectedCountsByType}
												rightSection={
													<div className={css(styleSheet.suggestionToggleContainer)}>
														<div className={css(styleSheet.suggestionToggleWrap)}>
															<Toggle
																checkedColor={THEME.colors.primary.main}
																id='show-suggestions-toggle'
																isOn={showSuggestionsToggleValue}
																onToggleCheckChanged={toggle}
																text={!showSuggestionsToggleValue ? 'Turn on suggestions!' : 'Turn off suggestions'}
																textStyles={[styleSheet.suggestionsToggleText]}
																uncheckedColor={THEME.colors.border.light}
															/>
														</div>
													</div>
												}
											/>
										)}
									</div>
								) : null}
								<div className={css(...computedCalendarContainerCardStyles)}>
									<MediaQueryObserver minWidth={1300}>
										{() => {
											return (
												<CampaignCalendar
													calendarType={calendarType}
													droppableType={props.droppableType || droppableType}
													initialStartDate={month}
													key={monthsToShow}
													numberOfMonthsToShow={monthsToShow}
													onApproveCampaignClicked={launchReviewAndApprove}
													onApproveSocialCampaignClicked={launchReviewAndApproveSocial}
													onCalendarTypeChanged={setCalendarType}
													onCampaignClicked={onCampaignClicked}
													onCampaignGroupClicked={props.onCampaignGroupClicked}
													onBlogPostClicked={onBlogPostClicked}
													onMonthsChange={onMonthsChange}
													onNotifyClientClicked={props.onNotifyClientClicked}
													ref={onCalendarRef}
													selectedUserId={selectedUserId}
													selectedStatus={selectedStatus}
													onUserFilterChanged={setSelectedUserId}
													onStatusFilterChanged={setSelectedStatus}
													onCampaignDataLoaded={onCampaignDataLoaded}
													onRenderFilterAccessory={() => (
														<div className={css(styleSheet.filterAccessoryContainer)}>
															<div className={css(styleSheet.filterDropdownContainer)}>
																{showSuggestionsToggleValue && (
																	<div>
																		<MultipleSelect
																			value={selectedIndustries}
																			onChange={(values: string[]) => setSelectedIndustries(values as Api.Industry[])}
																		>
																			<MultipleSelectTrigger styles={styleSheet.whiteTriggerBackground}>
																				<MultipleSelectValue
																					values={selectedIndustries}
																					placeholder='Select industries'
																					valueLabel={
																						selectedIndustries.length
																							? `${selectedIndustries.length} industries selected`
																							: undefined
																					}
																				/>
																			</MultipleSelectTrigger>
																			<MultipleSelectContent>
																				{industryOptions.map(industry => (
																					<MultipleSelectItem
																						key={industry}
																						value={industry}
																						selected={selectedIndustries.includes(industry)}
																					>
																						{getIndustryName(industry)}
																					</MultipleSelectItem>
																				))}
																			</MultipleSelectContent>
																		</MultipleSelect>
																	</div>
																)}

																{/* Content Calendar Filters */}
																{!props.hideUserFilter && (
																	<div className={css(styleSheet.filter)}>
																		<DefaultSelectBox
																			aria-label='Filter content by user'
																			onRenderSelectedOptionTitle={renderSelectedUserTitle}
																			onSelectionChanged={onUserFilterChanged}
																			options={userFilterOptions}
																			selectedOption={selectedUserOption}
																			triggerStyles={[styleSheet.selectBoxTrigger]}
																			optionStyles={[styleSheet.selectBoxOption]}
																		/>
																		<UserSelectModal
																			modalProps={{
																				isOpen: showingSentByModal,
																				onRequestClose: (user?: Api.UserViewModel, cancel?: boolean) => {
																					if (user && !cancel) {
																						setSelectedUserId(user.id);
																					}
																					setShowingSentByModal(false);
																				},
																			}}
																			title='Select an Employee'
																		/>
																	</div>
																)}
																<div className={css(styleSheet.filter)}>
																	<DefaultSelectBox
																		aria-label='Filter content by status'
																		onRenderSelectedOptionTitle={renderSelectedStatusTitle}
																		onSelectionChanged={onStatusFilterChanged}
																		options={statusFilterOptions}
																		selectedOption={
																			selectedStatus === null
																				? statusFilterOptions.find(x => x.value === StatusFilterOptions.All)
																				: statusFilterOptions.find(x => x.value === selectedStatus)
																		}
																		triggerStyles={[styleSheet.selectBoxTrigger]}
																		optionStyles={[styleSheet.selectBoxOption]}
																	/>
																</div>
																{props.onRenderHeaderAccessory?.()}

																{showSuggestionsToggleValue ? (
																	<Dropdown>
																		<DropdownSelectTrigger styles={[styleSheet.whiteTriggerBackground]}>
																			<div className={css(styleSheet.triggerContainer)}>
																				<span className={css(styleSheet.triggerValue)}>{selectedAccountAge?.text}</span>
																			</div>
																		</DropdownSelectTrigger>
																		<DropdownContent>
																			{CalendarAccountAgeOptions.map(option => (
																				<DropdownItem key={option.id} onClick={() => setSelectedAccountAge(option)}>
																					{option.text}
																				</DropdownItem>
																			))}
																		</DropdownContent>
																	</Dropdown>
																) : null}

																{showSuggestionsToggleValue && allBulkSelectableSuggestions?.length > 0 ? (
																	allBulkSelectableSuggestions.some(
																		s => !suggestionsContext.hasSelectedSuggestion(s)
																	) ? (
																		<Button
																			variant='secondary'
																			onClick={() => {
																				allBulkSelectableSuggestions.forEach(suggestion => {
																					suggestionsContext.selectSuggestion(suggestion);
																				});
																			}}
																		>
																			Select All
																		</Button>
																	) : (
																		<Button
																			variant='secondary'
																			onClick={() => suggestionsContext.clearSelectedSuggestions()}
																		>
																			Clear Selected
																		</Button>
																	)
																) : null}
															</div>

															{showSuggestionsToggleValue ? (
																<div className={css(styleSheet.suggestedButtonContainer)}>
																	{!!props.impersonationContext?.account?.id && (
																		<AiButton
																			title='Create AI Calendar'
																			onClick={() => {
																				appDrawerContext.setValue('ai-assistant');
																			}}
																		/>
																	)}

																	<button
																		className={css(bs.ctaButton, styleSheet.suggestedButton)}
																		onClick={suggestionsContext.scheduleSelectedSuggestions}
																		disabled={suggestionsContext.selectedSuggestions.length === 0}
																	>
																		Schedule suggested content
																	</button>
																</div>
															) : null}
														</div>
													)}
													onSocialMediaEventClicked={onSocialMediaEventClicked}
													onSuggestedEventClicked={onSuggestedEventClicked}
													onSuggestedSocialMediaEventClicked={onSuggestedSocialMediaEventClicked}
													options={props.calendarOptions}
													placeholders={placeholders}
													styles={[styleSheet.calendar]}
													suggestions={showSuggestionsToggleValue ? suggestionsForCampaignTypes : null}
													selectedCampaignType={templateBrowserCampaignType}
													campaignTypes={campaignTypes}
												/>
											);
										}}
									</MediaQueryObserver>
								</div>
							</div>

							{!props.hideTemplateBrowser && (
								<ContentCalendarTemplateBrowser
									ref={contentCalendarTemplateBrowserRef}
									dragDropProps={{
										droppableId: 'campaignTemplatesList',
										type: droppableType,
										draggingStyles: [
											calendarType === CalendarType.Month
												? styleSheet.campaignTemplateCardDraggingOverDay
												: styleSheet.campaignTemplateCardDragging,
										],
									}}
									isSuggestionsOn={showSuggestionsToggleValue}
									onTemplateTypeSelected={setTemplateBrowserCampaignType}
									onCampaignTemplateClicked={openCampaignComposer}
									onBlogPostEditClicked={onBlogPostClicked}
									selectedCampaignType={templateBrowserCampaignType}
									campaignTypes={campaignTypes}
									selectedUserId={selectedUserId}
									forUserId={selectedUserId}
								/>
							)}
						</>
					</DragDropContext>
				</div>
			</CampaignCalendarSuggestionsContext.Provider>
			<LocalNotificationObserver
				topic={Topics.APP_FULLSCREEN_MODAL_APPEARANCE}
				onNotificationReceived={onFullscreenModalLifecycleNotification}
			/>
			<LocalNotificationObserver
				onNotificationReceived={onEditCampaignNotificationReceived}
				topic={Topics.EDIT_CAMPAIGNS_ITEM}
			/>
			<LocalNotificationObserver
				onNotificationReceived={onRefreshFromNotification}
				topic={Topics.CREATE_CAMPAIGNS_ITEM}
			/>
			<LocalNotificationObserver onNotificationReceived={onRefreshFromNotification} topic={Topics.SEND_EMAIL} />
			<LocalNotificationObserver
				onNotificationReceived={onEditSocialNotificationReceived}
				topic={Topics.EDIT_SOCIAL_POST}
			/>
			<LocalNotificationObserver
				onNotificationReceived={onEditSocialNotificationReceived}
				topic={Topics.CREATE_SOCIAL_POST}
			/>
			<LocalNotificationObserver onNotificationReceived={onEditBlogPost} topic={Topics.EDIT_BLOG_POST} />

			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: !!showToggleSuggestionsOffConfirmation,
					onRequestClose: onCancelConfirmClose,
				}}
				options={DefaultConfirmationOptions}
				title='Turning off suggestions will unselect all suggestions'
			/>
			<DuplicateWarningDialog {...duplicateTemplateDialogProps} />

			<AiGeneratedContentSelectionModal
				isOpen={isAiGeneratedContentSelectionModalOpen}
				onClose={(isAiGeneratedContent: boolean, cancel?: boolean) => {
					if (cancel) {
						setIsAiGeneratedContentSelectionModalOpen(false);
						return;
					}

					confirmToggle();

					if (isAiGeneratedContent) {
						setShowSuggestionsAiGeneratedToggleValue(true);

						if (!hasGeneratedCalendar) {
							appDrawerContext.setValue('ai-assistant');
						}
					} else {
						setShowSuggestionsAiGeneratedToggleValue(false);
					}

					setIsAiGeneratedContentSelectionModalOpen(false);
				}}
			/>
		</div>
	);
});
const ContentCalendarAsObserver = observer(_ContentCalendar);
export const ContentCalendar = inject(ImpersonationContextKey)(ContentCalendarAsObserver);
